import { db } from '@/firebase/init'
import { useUserStore } from '@/stores/userStore'
import { ClosedDay } from '@team-milva/lab-models/resources'
import type { IClosedDay } from '@team-milva/lab-models/resources'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { defineStore } from 'pinia'

// Read-only mirror of lab-management's own closedDayStore — CA never creates,
// updates or deletes a ClosedDay, it only needs the lab's closed dates to
// exclude them from the hand-over day picker (see PlaceOrderView.vue).
export const useClosedDayStore = defineStore('closedDay', {
  state: () => ({
    allClosedDays: [] as ClosedDay[],
    unsubscribeAllClosedDays: null as (() => void) | null,
  }),

  actions: {
    getClosedDays(): Promise<ClosedDay[]> {
      if (this.allClosedDaysLoaded) {
        return Promise.resolve(this.allClosedDays)
      }

      const userStore = useUserStore()

      if (!userStore.currentUser) {
        return Promise.resolve([])
      }

      const labAccountID = userStore.currentUser.labAccountID

      return new Promise((resolve) => {
        let resolved = false

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'ClosedDays'),
          where('labAccountID', '==', labAccountID)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allClosedDays = querySnapshot.docs.map(
            (docSnap) => new ClosedDay(docSnap.data() as IClosedDay)
          )
          this.unsubscribeAllClosedDays = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(this.allClosedDays)
          }
        })
      })
    },

    resetStore(): void {
      this.unsubscribeAllClosedDays?.()
      this.allClosedDays = []
      this.unsubscribeAllClosedDays = null
    },
  },

  getters: {
    allClosedDaysLoaded: (state): boolean => !!state.unsubscribeAllClosedDays,

    // The lab's closed dates as plain ISO strings — what the hand-over day
    // picker's availability computation takes (see labDropPointAvailability.ts).
    closedDates: (state): string[] => state.allClosedDays.map((x) => x.date),
  },
})
